import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, BookOpen, Calculator, Leaf, Palette, Loader2, Archive, Trash2, Pencil } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const ICONS = { BookOpen, Calculator, Leaf, Palette };
const COLORS = {
  teal: { card: "bg-teal-50 text-teal-700 border-teal-200", dot: "bg-teal-500" },
  sky: { card: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  green: { card: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  amber: { card: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  indigo: { card: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  emerald: { card: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-600" },
  lime: { card: "bg-lime-50 text-lime-700 border-lime-200", dot: "bg-lime-500" },
  yellow: { card: "bg-yellow-50 text-yellow-700 border-yellow-200", dot: "bg-yellow-500" },
  orange: { card: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  red: { card: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  rose: { card: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  pink: { card: "bg-pink-50 text-pink-700 border-pink-200", dot: "bg-pink-500" },
  fuchsia: { card: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", dot: "bg-fuchsia-500" },
  purple: { card: "bg-purple-50 text-purple-700 border-purple-200", dot: "bg-purple-500" },
  violet: { card: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500" },
  blue: { card: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  cyan: { card: "bg-cyan-50 text-cyan-700 border-cyan-200", dot: "bg-cyan-500" },
  slate: { card: "bg-slate-50 text-slate-700 border-slate-200", dot: "bg-slate-500" },
  gray: { card: "bg-gray-50 text-gray-700 border-gray-200", dot: "bg-gray-500" },
  zinc: { card: "bg-zinc-50 text-zinc-700 border-zinc-200", dot: "bg-zinc-500" },
  neutral: { card: "bg-neutral-50 text-neutral-700 border-neutral-200", dot: "bg-neutral-500" },
  stone: { card: "bg-stone-50 text-stone-700 border-stone-200", dot: "bg-stone-500" },
  tealDeep: { card: "bg-teal-100 text-teal-800 border-teal-300", dot: "bg-teal-700" },
  blueDeep: { card: "bg-blue-100 text-blue-800 border-blue-300", dot: "bg-blue-700" },
  roseDeep: { card: "bg-rose-100 text-rose-800 border-rose-300", dot: "bg-rose-700" },
};
const EMPTY = { name: "", icon: "BookOpen", color: "teal", description: "", jenjang: [], schedule: "", materials: "" };

export default function Subjects() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delFor, setDelFor] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/subjects").then((r) => setSubjects(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, icon: s.icon, color: s.color, description: s.description || "", jenjang: s.jenjang || [], schedule: s.schedule || "", materials: s.materials || "" }); setEditId(s.id); setOpen(true); };

  const toggleJenjang = (k) => {
    setForm((f) => ({ ...f, jenjang: f.jenjang.includes(k) ? f.jenjang.filter((x) => x !== k) : [...f.jenjang, k].sort() }));
  };

  const save = async () => {
    if (!form.name) { toast.error("Nama mapel wajib diisi"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/subjects/${editId}`, form);
      else await api.post("/subjects", form);
      toast.success("Mata pelajaran disimpan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    await api.delete(`/subjects/${delFor.id}`);
    toast.success("Mata pelajaran dihapus");
    setDelFor(null);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <ConfirmDialog open={!!delFor} onOpenChange={(v) => !v && setDelFor(null)} title={`Hapus mapel "${delFor?.name}"?`}
        description="Semua tugas dan nilai murid di mata pelajaran ini akan ikut terhapus permanen." onConfirm={remove} testId="delete-subject-dialog" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">Mata Pelajaran</h1>
          <p className="text-slate-500 mt-1">Atur mapel, jenjang kelas, jadwal, dan materi.</p>
        </div>
        <Button onClick={openNew} className="rounded-xl h-11 bg-teal-600 hover:bg-teal-700" data-testid="add-subject-btn">
          <Plus className="w-5 h-5 mr-2" /> Tambah Mapel
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : subjects.length === 0 ? (
        <div className="text-center py-20 text-slate-400" data-testid="subjects-empty-state">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Belum ada mata pelajaran. Klik "Tambah Mapel" untuk membuat.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {subjects.map((s) => {
            const Icon = ICONS[s.icon] || BookOpen;
            return (
              <div key={s.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all" data-testid={`subject-card-${s.id}`}>
                <button onClick={() => navigate(`/guru/mapel/${s.id}`)} className="text-left w-full" data-testid={`open-subject-${s.id}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${(COLORS[s.color] || COLORS.teal).card}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-800 mt-3 hover:text-teal-700 transition-colors">{s.name}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mt-1">{s.description || "—"}</p>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {(s.jenjang || []).map((k) => <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Kls {k}</span>)}
                  </div>
                  <p className="text-xs text-teal-600 font-medium mt-3 inline-flex items-center gap-1">Lihat tugas & materi →</p>
                </button>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => openEdit(s)} data-testid={`edit-subject-${s.id}`}><Pencil className="w-4 h-4 mr-1" /> Ubah</Button>
                  <Button size="sm" variant="outline" className="rounded-lg text-rose-600" onClick={() => setDelFor(s)} data-testid={`delete-subject-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Ubah" : "Tambah"} Mata Pelajaran</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Mapel</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="subject-name-input" /></div>
            <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ikon</Label>
                <div className="flex gap-2 mt-1">
                  {Object.keys(ICONS).map((k) => { const I = ICONS[k]; return (
                    <button key={k} type="button" onClick={() => setForm({ ...form, icon: k })} className={`p-2.5 rounded-xl border ${form.icon === k ? "border-teal-500 bg-teal-50" : "border-slate-200"}`}><I className="w-5 h-5 text-slate-700" /></button>
                  ); })}
                </div>
              </div>
              <div>
                <Label>Warna</Label>
                <div className="flex flex-wrap gap-2 mt-1 max-w-md" data-testid="subject-color-picker">
                  {Object.keys(COLORS).map((c) => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} data-testid={`color-${c}`} title={c} className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${form.color === c ? "border-slate-800 ring-2 ring-slate-300" : "border-white shadow-sm"} ${COLORS[c].dot}`} />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label>Jenjang Kelas</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {[1, 2, 3, 4, 5, 6].map((k) => (
                  <button key={k} type="button" onClick={() => toggleJenjang(k)} className={`px-3 py-1.5 rounded-lg text-sm border ${form.jenjang.includes(k) ? "bg-teal-600 text-white border-teal-600" : "border-slate-200 text-slate-600"}`} data-testid={`jenjang-${k}`}>Kelas {k}</button>
                ))}
              </div>
            </div>
            <div><Label>Jadwal</Label><Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} className="mt-1 rounded-xl" placeholder="Senin & Rabu, 08.00" /></div>
            <div><Label>Materi</Label><Textarea value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} className="mt-1 rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="save-subject-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
